import React, { useState, useRef } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import usePageTitle from "@/hooks/usePageTitle";
import {
    ArrowRight, Loader2, CheckCircle2, Upload, X, FileText,
    Building2, User, ShieldCheck, PenLine, Trash2,
} from "lucide-react";

const ENTITY_TYPES = ["LLC", "Sole Proprietorship", "Corporation", "S-Corporation", "Partnership", "Non-Profit"];
const RENT_OWN = ["Own", "Rent", "Mortgage/Owned"];
const INDUSTRIES = [
    "Construction", "Retail", "Restaurant / Hospitality", "Healthcare", "Manufacturing",
    "Transportation / Logistics", "Professional Services", "Technology / SaaS",
    "Auto / Repair", "Wholesale", "E-commerce", "Real Estate",
    "Beauty / Salon", "Firearms / gun dealers", "Other",
];
const USES = [
    "Working capital", "Purchase Inventory", "Buy Equipment", "Expansion",
    "Payroll", "Marketing", "Refinance debt", "Real estate", "Other",
];
const CREDIT_BANDS = ["740+", "680–739", "620–679", "580–619", "<580", "Not sure"];
const ALLOWED_EXT = /\.(pdf|jpe?g|png|heic|heif)$/i;
const MAX_SIZE = 15 * 1024 * 1024;

const inputClass =
    "w-full h-11 px-3.5 bg-white border-2 border-black/10 focus:border-[#FF6B1A] outline-none rounded-none text-[14px] transition-colors";
const labelClass = "text-xs uppercase tracking-[0.14em] font-semibold text-black/60 mb-1.5 block";

function Section({ icon: Icon, title, subtitle, children }) {
    return (
        <div className="mb-14">
            <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 flex items-center justify-center border-2 border-[#FF6B1A] bg-white shrink-0">
                    <Icon className="text-[#FF6B1A]" size={20} />
                </div>
                <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight leading-tight">{title}</h2>
                    {subtitle && <p className="text-sm text-black/60 mt-1">{subtitle}</p>}
                </div>
            </div>
            <div className="pl-0 md:pl-14">{children}</div>
        </div>
    );
}

export default function Apply() {
    usePageTitle("Business Funding Application", "Apply for working capital with Lended. Most decisions in 24 hours. No impact to credit at this stage.");

    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [files, setFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const [showOwner2, setShowOwner2] = useState(false);
    const [uses, setUses] = useState([]);
    const fileInputRef = useRef(null);

    const [form, setForm] = useState({
        legal_name: "", entity_type: "", ein: "", business_phone: "", website: "",
        business_address: "", start_date: "", rent_or_own: "", industry: "",
        open_balances: "No", description: "", avg_monthly_revenue: "", requested_amount: "",
        // owner 1
        o1_full_name: "", o1_email: "", o1_mobile_phone: "", o1_ownership_pct: "",
        o1_ssn: "", o1_dob: "", o1_home_address: "", o1_credit_score: "",
        // owner 2
        o2_full_name: "", o2_email: "", o2_mobile_phone: "", o2_ownership_pct: "",
        o2_ssn: "", o2_dob: "", o2_home_address: "", o2_credit_score: "",
        // signature
        typed_signature: "", authorized: false,
    });

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const chk = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

    const toggleUse = (u) => {
        setUses((prev) => prev.includes(u) ? prev.filter((x) => x !== u) : [...prev, u]);
    };

    const addFiles = (list) => {
        const good = [];
        const errs = [];
        for (const f of list) {
            if (!ALLOWED_EXT.test(f.name)) { errs.push(`${f.name}: unsupported type`); continue; }
            if (f.size > MAX_SIZE) { errs.push(`${f.name}: over 15MB`); continue; }
            good.push(f);
        }
        if (errs.length) toast.error(errs.join(" · "));
        setFiles((prev) => [...prev, ...good]);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        addFiles(Array.from(e.dataTransfer.files || []));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.authorized) { toast.error("Please agree to the authorization"); return; }
        if (!form.typed_signature.trim()) { toast.error("Please type your full name as signature"); return; }
        if (files.length === 0) {
            if (!confirm("No bank statements attached. Submit anyway? (Underwriting requires 4 months)")) return;
        }
        setLoading(true);
        try {
            const fd = new FormData();
            const text = {
                ...form,
                use_of_funds: JSON.stringify(uses),
                authorized: form.authorized ? "true" : "false",
                signed_date: new Date().toISOString().slice(0, 10),
            };
            Object.entries(text).forEach(([k, v]) => fd.append(k, v ?? ""));
            files.forEach((f) => fd.append("documents", f, f.name));

            const res = await fetch(`${API}/applications`, { method: "POST", body: fd });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Submission failed");
            }
            setDone(true);
            toast.success("Application received");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(err.message || "Submission failed");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <section className="bg-white py-24 min-h-[70vh]" data-testid="apply-success-page">
                <div className="container-lended max-w-2xl text-center">
                    <CheckCircle2 className="mx-auto text-[#FF6B1A]" size={72} />
                    <h1 className="font-display text-5xl md:text-6xl font-extrabold mt-6 tracking-[-0.03em]">
                        Application received.
                    </h1>
                    <p className="text-lg text-black/70 mt-6">
                        Thank you, <strong>{form.o1_full_name}</strong>. A funding specialist will review your file
                        and reach out within one business hour. For urgent questions call{" "}
                        <a href="tel:5167305656" className="text-[#FF6B1A] font-semibold">516-730-5656</a> or
                        email <a href="mailto:submissions@lended.com" className="text-[#FF6B1A] font-semibold">submissions@lended.com</a>.
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-white py-14 md:py-20" data-testid="apply-page">
            <div className="container-lended max-w-4xl">
                {/* Header */}
                <div className="mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#FF6B1A] text-[#FF6B1A] text-xs font-bold uppercase tracking-[0.16em]">
                        Free · No impact to credit at this stage
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.04em] leading-[0.95] mt-6">
                        Business Funding <br />
                        <span className="text-[#FF6B1A]">Application</span>
                    </h1>
                    <p className="text-lg text-black/70 mt-6 max-w-2xl">
                        Tell us about your business and the capital you need. Most decisions inside 24 hours.
                        Information stays confidential.
                    </p>
                </div>

                <form onSubmit={submit} data-testid="apply-form">
                    {/* Business */}
                    <Section icon={Building2} title="Business Information" subtitle="The company applying for funding.">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className={labelClass}>Business legal name *</label>
                                <input required value={form.legal_name} onChange={upd("legal_name")} className={inputClass} data-testid="app-legal-name" />
                            </div>
                            <div>
                                <label className={labelClass}>Legal entity *</label>
                                <select required value={form.entity_type} onChange={upd("entity_type")} className={inputClass} data-testid="app-entity">
                                    <option value="">Select…</option>
                                    {ENTITY_TYPES.map((e) => <option key={e}>{e}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Federal Tax ID (EIN) *</label>
                                <input required value={form.ein} onChange={upd("ein")} placeholder="XX-XXXXXXX" className={inputClass} data-testid="app-ein" />
                            </div>
                            <div>
                                <label className={labelClass}>Business phone *</label>
                                <input required value={form.business_phone} onChange={upd("business_phone")} className={inputClass} data-testid="app-biz-phone" />
                            </div>
                            <div>
                                <label className={labelClass}>Website (optional)</label>
                                <input value={form.website} onChange={upd("website")} className={inputClass} data-testid="app-website" />
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelClass}>Business address *</label>
                                <input required value={form.business_address} onChange={upd("business_address")} className={inputClass} data-testid="app-biz-address" />
                            </div>
                            <div>
                                <label className={labelClass}>Start date *</label>
                                <input required type="date" value={form.start_date} onChange={upd("start_date")} className={inputClass} data-testid="app-start-date" />
                            </div>
                            <div>
                                <label className={labelClass}>Rent or own?</label>
                                <select value={form.rent_or_own} onChange={upd("rent_or_own")} className={inputClass} data-testid="app-rent-own">
                                    <option value="">Select…</option>
                                    {RENT_OWN.map((e) => <option key={e}>{e}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Industry *</label>
                                <select required value={form.industry} onChange={upd("industry")} className={inputClass} data-testid="app-industry">
                                    <option value="">Select industry…</option>
                                    {INDUSTRIES.map((e) => <option key={e}>{e}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Any open balances?</label>
                                <select value={form.open_balances} onChange={upd("open_balances")} className={inputClass} data-testid="app-open-balances">
                                    <option>No</option>
                                    <option>Yes</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelClass}>Business description *</label>
                                <textarea required rows={3} value={form.description} onChange={upd("description")} className={`${inputClass} h-auto py-3`} data-testid="app-description" />
                            </div>
                            <div>
                                <label className={labelClass}>Avg. monthly revenue (USD) *</label>
                                <input required value={form.avg_monthly_revenue} onChange={upd("avg_monthly_revenue")} placeholder="$ amount" className={inputClass} data-testid="app-revenue" />
                            </div>
                            <div>
                                <label className={labelClass}>Requested funding amount (USD) *</label>
                                <input required value={form.requested_amount} onChange={upd("requested_amount")} placeholder="$ amount" className={inputClass} data-testid="app-funding" />
                            </div>
                            <div className="md:col-span-2 mt-2">
                                <label className={labelClass}>Use of funds (select all that apply)</label>
                                <div className="flex flex-wrap gap-2">
                                    {USES.map((u) => (
                                        <button
                                            key={u} type="button"
                                            onClick={() => toggleUse(u)}
                                            data-testid={`app-use-${u.toLowerCase().replace(/\s+/g, '-')}`}
                                            className={`text-xs font-semibold uppercase tracking-wider px-4 py-2 border-2 transition ${
                                                uses.includes(u)
                                                    ? "bg-[#FF6B1A] border-[#FF6B1A] text-white"
                                                    : "bg-white border-black/15 text-black/70 hover:border-[#FF6B1A]"
                                            }`}
                                        >
                                            {u}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Section>

                    {/* Owner 1 */}
                    <Section icon={User} title="Owner Information" subtitle="Primary owner (20%+ stake).">
                        <OwnerFields prefix="o1" form={form} onChange={upd} testIdPrefix="app-o1" required />
                    </Section>

                    {/* Owner 2 toggle */}
                    <div className="mb-14 pl-0 md:pl-14">
                        {!showOwner2 ? (
                            <button type="button" onClick={() => setShowOwner2(true)} className="btn-outline-black text-sm" data-testid="add-owner-2">
                                + Add a second owner
                            </button>
                        ) : (
                            <div className="bg-[#F8F5F2] p-6 md:p-8 border-l-4 border-[#FF6B1A]">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="font-display text-xl font-bold">Second owner</h3>
                                    <button type="button" onClick={() => setShowOwner2(false)} className="text-black/55 hover:text-black" data-testid="remove-owner-2">
                                        <X size={18} />
                                    </button>
                                </div>
                                <OwnerFields prefix="o2" form={form} onChange={upd} testIdPrefix="app-o2" required={false} />
                            </div>
                        )}
                    </div>

                    {/* Documents */}
                    <Section icon={Upload} title="Bank statements & documents" subtitle="Attach your last 4 months of business bank statements (PDF or image). REQUIRED for underwriting.">
                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed p-10 text-center cursor-pointer transition ${
                                dragOver ? "border-[#FF6B1A] bg-[#FFF4EC]" : "border-black/20 hover:border-[#FF6B1A] bg-[#F8F5F2]"
                            }`}
                            data-testid="file-dropzone"
                        >
                            <Upload className="mx-auto text-[#FF6B1A]" size={32} />
                            <p className="font-display font-bold text-lg mt-3">Drop your last 4 months of business bank statements here</p>
                            <p className="text-xs text-black/55 mt-2">PDF, JPG, PNG, HEIC · up to 15MB per file · click or drag &amp; drop</p>
                            <input
                                ref={fileInputRef} type="file" multiple
                                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                                className="hidden"
                                onChange={(e) => addFiles(Array.from(e.target.files || []))}
                                data-testid="file-input"
                            />
                        </div>
                        {files.length > 0 && (
                            <ul className="mt-4 space-y-2" data-testid="file-list">
                                {files.map((f, i) => (
                                    <li key={i} className="flex items-center gap-3 bg-white border-2 border-black/10 p-3">
                                        <FileText className="text-[#FF6B1A] shrink-0" size={18} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold truncate">{f.name}</div>
                                            <div className="text-xs text-black/55">{(f.size / 1024).toFixed(0)} KB</div>
                                        </div>
                                        <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-black/55 hover:text-red-600" data-testid={`remove-file-${i}`}>
                                            <Trash2 size={16} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Section>

                    {/* Signature */}
                    <Section icon={PenLine} title="Verification & Signature" subtitle="Read, agree, sign.">
                        <div className="bg-[#F8F5F2] p-6 md:p-8 border-l-4 border-[#FF6B1A] mb-6 text-sm text-black/75 leading-relaxed">
                            <p>
                                By signing this application, I affirm the accuracy and truthfulness of all provided
                                information. I grant <strong>Lended</strong> and its representatives permission to obtain
                                consumer and/or personal, business, and investigative reports for commercial loans,
                                including daily repayment features or future receivables purchases. This authorization
                                extends to accessing information from TransUnion, Experian, Equifax, other credit bureaus,
                                banks, creditors, and third parties. I also authorize the sharing of this application
                                and related information with the involved parties.
                            </p>
                            <p className="mt-3">
                                I consent to the release of information by creditors or financial institutions to
                                Lended and its representatives. I provide my business cell phone number and email
                                address, consenting to receive correspondence and messages regarding transactions.
                                I understand I may receive text messages, with an anticipated frequency of approximately
                                10 per month, and acknowledge that consent to receive texts is not a condition of approval.
                                I certify the completeness, truth, and accuracy of all provided information.
                            </p>
                        </div>
                        <label className="flex items-start gap-3 text-sm text-black/75 mb-5 cursor-pointer" data-testid="auth-agree-label">
                            <input type="checkbox" checked={form.authorized} onChange={chk("authorized")} className="mt-0.5 w-4 h-4 accent-[#FF6B1A]" required data-testid="auth-agree" />
                            <span>I have read and agree to the authorization above. *</span>
                        </label>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Signature (type your full name) *</label>
                                <input required value={form.typed_signature} onChange={upd("typed_signature")} className={`${inputClass} font-mono text-base`} data-testid="app-signature" />
                            </div>
                            <div>
                                <label className={labelClass}>Date</label>
                                <input readOnly value={new Date().toISOString().slice(0, 10)} className={`${inputClass} bg-black/5`} />
                            </div>
                        </div>
                    </Section>

                    <div className="pl-0 md:pl-14">
                        <div className="flex items-start gap-3 bg-[#F8F5F2] p-4 border-l-4 border-[#FF6B1A] mb-6">
                            <ShieldCheck className="text-[#FF6B1A] shrink-0 mt-0.5" size={18} />
                            <p className="text-xs text-black/70">
                                Submitting does not impact your personal credit. A team member contacts you before any hard pull.
                            </p>
                        </div>
                        <button type="submit" disabled={loading} className="btn-orange text-base disabled:opacity-60" data-testid="submit-application">
                            {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
                            {loading ? "Submitting…" : "Submit application"}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}

function OwnerFields({ prefix, form, onChange, testIdPrefix, required }) {
    const req = required ? "*" : "";
    const R = required;
    return (
        <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <label className={labelClass}>Full name {req}</label>
                <input required={R} value={form[`${prefix}_full_name`]} onChange={onChange(`${prefix}_full_name`)} className={inputClass} data-testid={`${testIdPrefix}-name`} />
            </div>
            <div>
                <label className={labelClass}>Email {req}</label>
                <input required={R} type="email" value={form[`${prefix}_email`]} onChange={onChange(`${prefix}_email`)} className={inputClass} data-testid={`${testIdPrefix}-email`} />
            </div>
            <div>
                <label className={labelClass}>Mobile phone {req}</label>
                <input required={R} value={form[`${prefix}_mobile_phone`]} onChange={onChange(`${prefix}_mobile_phone`)} className={inputClass} data-testid={`${testIdPrefix}-phone`} />
            </div>
            <div>
                <label className={labelClass}>% of ownership</label>
                <input value={form[`${prefix}_ownership_pct`]} onChange={onChange(`${prefix}_ownership_pct`)} placeholder="e.g. 100" className={inputClass} data-testid={`${testIdPrefix}-pct`} />
            </div>
            <div>
                <label className={labelClass}>Social Security Number {req}</label>
                <input required={R} value={form[`${prefix}_ssn`]} onChange={onChange(`${prefix}_ssn`)} placeholder="XXX-XX-XXXX" className={inputClass} data-testid={`${testIdPrefix}-ssn`} />
                <p className="text-xs text-black/50 mt-1">Encrypted in transit. Only authorized staff can view.</p>
            </div>
            <div>
                <label className={labelClass}>Date of birth {req}</label>
                <input required={R} type="date" value={form[`${prefix}_dob`]} onChange={onChange(`${prefix}_dob`)} className={inputClass} data-testid={`${testIdPrefix}-dob`} />
            </div>
            <div className="md:col-span-2">
                <label className={labelClass}>Home address {req}</label>
                <input required={R} value={form[`${prefix}_home_address`]} onChange={onChange(`${prefix}_home_address`)} className={inputClass} data-testid={`${testIdPrefix}-home`} />
            </div>
            <div>
                <label className={labelClass}>Estimated credit score</label>
                <select value={form[`${prefix}_credit_score`]} onChange={onChange(`${prefix}_credit_score`)} className={inputClass} data-testid={`${testIdPrefix}-credit`}>
                    <option value="">Select…</option>
                    {CREDIT_BANDS.map((c) => <option key={c}>{c}</option>)}
                </select>
            </div>
        </div>
    );
}
